import {
  PayloadAction,
  createSelector,
  createSlice,
  isAnyOf
} from '@reduxjs/toolkit';
import {
  SegmentChangedPayload,
  Segment,
  SegmentFilter,
  SegmentFilterChangedPayload,
  SegmentFilterBeingEditedChangedPayload,
  SegmentFilterBeingEdited,
  SegmentFilterDeletedPayload
} from './segment-controls.types';
import { RootState } from 'app/store';
import { createSegment } from './factories/segment-factory';
import { advancedReportQueryApi } from 'app/apis/advanced-reports/advanced-report-query-api';
import { selectAttributionModel } from 'features/attribute-model/availableAttributionModelsSlice';
import { changeDateRange } from 'features/date-range-selection/dateRangeSlice';
import { createSegmentFilter } from './factories/segment-filter-factory';
import { PURGE } from 'redux-persist';
import { selectSite } from 'features/topbar/selectedSiteSlice';
import { selectAttributionCompareModel } from 'features/attribute-compare-model/availableAttributionCompareModelsSlice';
import { SegmentControlsFilterValueOperators } from 'features/segment-controls/constants/segment-controls-filter-value-operators';
import { resetAdvancedReports } from 'features/advanced-reports/advanced-reports.middleware';
import { resetInsights } from 'features/insights/insights.middleware';
import { queryApi } from 'app/apis/queryApi';

interface SelectedSegmentControls {
  segments: Segment[];
  segmentFilters: SegmentFilter[];
  isDirty: boolean;
  segmentFilterBeingEdited?: SegmentFilter;
}

interface SegmentControlsState {
  componentId?: string;
  selectedSegmentControls: SelectedSegmentControls;
}

const initialState = {
  pageSegmentControls: [
    {
      componentId: 'AdvancedReports',
      selectedSegmentControls: {
        segments: [createSegment(0)],
        segmentFilters: [],
        isDirty: false
      }
    },
    {
      componentId: 'ConversionInfluence',
      selectedSegmentControls: {
        segments: [createSegment(0)],
        segmentFilters: [],
        isDirty: false
      }
    }
  ] as SegmentControlsState[]
};

const segmentControlsSlice = createSlice({
  name: 'segmentControls',
  initialState,
  reducers: {
    onSegmentChanged: (state, action: PayloadAction<SegmentChangedPayload>) => {
      const selectedSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === action.payload.componentId
      );
      if (selectedSegmentControls) {
        const appendedSegments = [
          ...selectedSegmentControls.selectedSegmentControls.segments.filter(
            (segment) => segment.id !== action.payload.segment.id
          ),
          action.payload.segment
        ];

        selectedSegmentControls.selectedSegmentControls.segments =
          appendedSegments as Segment[];
        selectedSegmentControls.selectedSegmentControls.isDirty = true;
      }
    },
    onSegmentAdded: (state, action: PayloadAction<SegmentChangedPayload>) => {
      const selectedSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === action.payload.componentId
      );

      if (selectedSegmentControls) {
        selectedSegmentControls.selectedSegmentControls.segments = [
          ...selectedSegmentControls.selectedSegmentControls.segments,
          action.payload.segment
        ];
        selectedSegmentControls.selectedSegmentControls.isDirty = true;
      }
    },
    onSegmentDeleted: (state, action: PayloadAction<SegmentChangedPayload>) => {
      const selectedSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === action.payload.componentId
      );

      if (selectedSegmentControls) {
        selectedSegmentControls.selectedSegmentControls.segments =
          selectedSegmentControls.selectedSegmentControls.segments.filter(
            (segment) => segment.id !== action.payload.segment.id
          );
        selectedSegmentControls.selectedSegmentControls.segmentFilters =
          selectedSegmentControls.selectedSegmentControls.segmentFilters.filter(
            (filter) => filter.segmentId !== action.payload.segment.id
          );
        selectedSegmentControls.selectedSegmentControls.isDirty = true;
      }
    },
    onSegmentFilterAdded: (
      state,
      action: PayloadAction<SegmentFilterChangedPayload>
    ) => {
      const selectedSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === action.payload.componentId
      );
      if (selectedSegmentControls) {
        const highestExistingOrder =
          selectedSegmentControls.selectedSegmentControls.segmentFilters
            .filter((filter) => filter.segmentId === action.payload.segment?.id)
            .map((filter) => filter.order)
            .max();

        const newSegmentFilter = createSegmentFilter(
          action.payload.segment!.id,
          highestExistingOrder + 1,
          '',
          SegmentControlsFilterValueOperators.EQUALS
        );

        selectedSegmentControls.selectedSegmentControls.segmentFilterBeingEdited =
          newSegmentFilter;
      }
    },
    onSegmentFilterChanged: (
      state,
      action: PayloadAction<SegmentFilterChangedPayload>
    ) => {
      const selectedSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === action.payload.componentId
      );

      if (selectedSegmentControls) {
        const newFilters = [
          ...selectedSegmentControls.selectedSegmentControls.segmentFilters.filter(
            (filter) =>
              filter.id !==
              selectedSegmentControls.selectedSegmentControls
                .segmentFilterBeingEdited!.id
          ),
          selectedSegmentControls.selectedSegmentControls
            .segmentFilterBeingEdited!
        ];

        selectedSegmentControls.selectedSegmentControls.segmentFilters =
          newFilters as SegmentFilter[];
        selectedSegmentControls.selectedSegmentControls.isDirty = true;
        selectedSegmentControls.selectedSegmentControls.segmentFilterBeingEdited =
          undefined;
      }
    },
    onSegmentFilterDeleted: (
      state,
      action: PayloadAction<SegmentFilterDeletedPayload>
    ) => {
      const selectedSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === action.payload.componentId
      );

      if (selectedSegmentControls) {
        selectedSegmentControls.selectedSegmentControls.segmentFilters =
          selectedSegmentControls.selectedSegmentControls.segmentFilters.filter(
            (filter) => filter.id !== action.payload.segmentFilterId
          );
        selectedSegmentControls.selectedSegmentControls.isDirty = true;
      }
    },
    onSegmentFilterBeingEditedChanged: (
      state,
      action: PayloadAction<SegmentFilterBeingEditedChangedPayload>
    ) => {
      const selectedSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === action.payload.componentId
      );
      if (selectedSegmentControls) {
        selectedSegmentControls.selectedSegmentControls.segmentFilterBeingEdited =
          action.payload.segmentFilter;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
    builder.addCase(selectSite, () => initialState);
    builder.addCase(resetAdvancedReports, () => initialState);
    builder.addCase(resetInsights, () => initialState);
    builder.addCase(selectAttributionModel, (state) => {
      // Only applicable to advanced reports
      const advancedReportsSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === 'AdvancedReports'
      );
      if (advancedReportsSegmentControls) {
        advancedReportsSegmentControls.selectedSegmentControls.isDirty = true;
      }
    });
    builder.addCase(selectAttributionCompareModel, (state) => {
      // Only applicable to advanced reports
      const advancedReportsSegmentControls = state.pageSegmentControls.find(
        (pageSegmentControls) =>
          pageSegmentControls.componentId === 'AdvancedReports'
      );
      if (advancedReportsSegmentControls) {
        advancedReportsSegmentControls.selectedSegmentControls.isDirty = true;
      }
    });
    builder.addCase(changeDateRange, (state) => {
      // Applicable to both advanced reports and insights
      state.pageSegmentControls.forEach((pageSegmentControl) => {
        pageSegmentControl.selectedSegmentControls.isDirty = true;
      });
    });
    builder.addMatcher(
      queryApi.endpoints.getConversionInfluenceInsights.matchFulfilled,
      (state) => {
        const advancedReportsSegmentControls = state.pageSegmentControls.find(
          (pageSegmentControls) =>
            pageSegmentControls.componentId === 'ConversionInfluence'
        );
        if (advancedReportsSegmentControls) {
          advancedReportsSegmentControls.selectedSegmentControls.isDirty =
            false;
        }
      }
    );
    builder.addMatcher(
      isAnyOf(
        advancedReportQueryApi.endpoints.getClicksAdvancedReport.matchFulfilled,
        advancedReportQueryApi.endpoints.getConversionAdvancedReport
          .matchFulfilled,
        advancedReportQueryApi.endpoints.getOpportunityAdvancedReport
          .matchFulfilled,
        advancedReportQueryApi.endpoints.getRevenueAdvancedReport
          .matchFulfilled,
        advancedReportQueryApi.endpoints.getCostAdvancedReport.matchFulfilled
      ),
      (state) => {
        // Only applicable to advanced reports
        const advancedReportsSegmentControls = state.pageSegmentControls.find(
          (pageSegmentControls) =>
            pageSegmentControls.componentId === 'AdvancedReports'
        );
        if (advancedReportsSegmentControls) {
          advancedReportsSegmentControls.selectedSegmentControls.isDirty =
            false;
        }
      }
    );
  }
});

const { actions, reducer } = segmentControlsSlice;

export const {
  onSegmentChanged,
  onSegmentAdded,
  onSegmentDeleted,
  onSegmentFilterAdded,
  onSegmentFilterChanged,
  onSegmentFilterDeleted,
  onSegmentFilterBeingEditedChanged
} = actions;

export default reducer;

const selectSelf = (state: RootState) => state.segmentControls;
const selectSegmentControlStateByComponentId = (
  state: RootState,
  componentId: string
) => componentId;

export const selectSegmentControlsStateByComponentId = createSelector(
  selectSelf,
  selectSegmentControlStateByComponentId,
  (segmentControls, componentId) => {
    const selectedSegmentControls = segmentControls.pageSegmentControls.find(
      (pageSegmentControls) => pageSegmentControls.componentId === componentId
    );

    return selectedSegmentControls;
  }
);

export const selectSegments = createSelector(
  selectSegmentControlsStateByComponentId,
  (state) => state?.selectedSegmentControls.segments ?? []
);

export const areSegmentsInitialState = createSelector(
  selectSegments,
  (segments) => segments.length === 1 && segments[0].segmentCategory === ''
);

export const selectSegmentsOrdered = createSelector(
  selectSegments,
  (segments) =>
    segments.map((segment) => segment).sort((a, b) => a.order - b.order)
);

export const selectHighestSegmentOrder = createSelector(
  selectSegments,
  (segments) => segments.map((segment) => segment.order).max()
);

export const selectAreSegmentsValid = createSelector(
  selectSegments,
  (segments) => segments.every((segment) => segment.segmentCategory !== '')
);

export const selectIsDirty = createSelector(
  selectSegmentControlsStateByComponentId,
  (state) => state?.selectedSegmentControls.isDirty
);

export const selectCanRunQuery = createSelector(
  selectSegmentControlsStateByComponentId,
  selectAreSegmentsValid,
  (state, areSegmentsValid) =>
    state?.selectedSegmentControls.isDirty && areSegmentsValid
);

export const selectCanAddSegment = createSelector(
  selectSegments,
  selectAreSegmentsValid,
  (segments, areSegmentsValid) => segments.length <= 5 && areSegmentsValid
);

export const selectValidSegments = createSelector(selectSegments, (segments) =>
  segments
    .filter((segment) => segment.segmentCategory !== '')
    .map((segment) => segment)
);

export const selectSegmentCategories = createSelector(
  selectSegmentsOrdered,
  (segments) => segments.map((segment) => segment.segmentCategory)
);

export const selectSegmentFilters = createSelector(
  selectSegmentControlsStateByComponentId,
  (state) =>
    state?.selectedSegmentControls.segmentFilters
      .map((filter) => filter)
      .sort((a, b) => a.order - b.order)
);

export const selectHasSegmentFilters = createSelector(
  selectSegmentControlsStateByComponentId,
  (state) => (state?.selectedSegmentControls.segmentFilters.length ?? 0) > 0
);

export const selectFilterBeingEdited = createSelector(
  selectSegmentControlsStateByComponentId,
  (state) => {
    if (!state?.selectedSegmentControls.segmentFilterBeingEdited) {
      return undefined;
    }

    const existingFilterIndex =
      state.selectedSegmentControls.segmentFilters.findIndex(
        (filter) =>
          filter.id ===
          state.selectedSegmentControls.segmentFilterBeingEdited!.id
      );

    let titlePrefix = 'Filter Control - ';

    if (existingFilterIndex < 0) {
      titlePrefix += ' Add';
    } else {
      titlePrefix += ' Edit';
    }

    const segmentHasExistingFilters =
      state.selectedSegmentControls.segmentFilters.filter(
        (filter) =>
          filter.segmentId ===
          state.selectedSegmentControls.segmentFilterBeingEdited?.segmentId
      ).length > 0;

    if (segmentHasExistingFilters && existingFilterIndex !== 0) {
      titlePrefix += ' And Condition';
    }

    const filterBeingEdited: SegmentFilterBeingEdited = {
      ...state.selectedSegmentControls.segmentFilterBeingEdited,
      title: titlePrefix
    };

    return filterBeingEdited;
  }
);

export const selectSegmentFiltersBySegmentId = createSelector(
  [
    (state: RootState) => state.segmentControls,
    (state: RootState, segmentId: string, componentId: string) => ({
      segmentId,
      componentId
    })
  ],
  (segmentControls, { segmentId, componentId }) => {
    const selectedSegmentControls = segmentControls.pageSegmentControls.find(
      (pageSegmentControls) => pageSegmentControls.componentId === componentId
    );

    return selectedSegmentControls?.selectedSegmentControls.segmentFilters.filter(
      (filter) => filter.segmentId === segmentId
    );
  }
);
